import { Template, useContent } from '@wix/funnel-builder-lizard'
import React, { ComponentProps, FC, PropsWithChildren, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { PageLogger, usePageLogger } from '../../bi/mfi'
import { useFinishFlowV2 } from '../../hooks/useFinishFlowV2'
import { useFlowContext } from '../../hooks/useFlowContext'
import {
  BrandedAppCategory,
  BrandedAppSiteSelctor,
  BrandedAppWelcome,
  Category,
  CategoryNew,
  CustomizeSiteEqualSplit,
  EasierCategory,
  EnterpriseForm,
  EnterpriseFormThanks,
  EnterpriseFormWelcome,
  HarmonyMobileBlock,
  HarmonySplit,
  HarmonyTemplateSaved,
  HeadlessApps,
  HeadlessBusinessName,
  HeadlessMobileSiteCreation,
  HeadlessRadioInput,
  HeadlessTwoRadioInputs,
  OldUserSplitScreen,
  ReturningUserSplit,
  SGLandingMobile,
  Sorry,
  SorryNew,
  StoresFunnelBusinessName,
  StoresFunnelChooseCategory,
  StoresFunnelChooseTemplate,
  StoresFunnelLoader,
  StoresFunnelUpgrade,
  Transition,
  Welcome,
  WelcomeV2,
  WixelSplit,
  WixIntrosBoxXL,
  WixMobileApps,
  WixMobileBaseCheckbox,
  WixMobileBusinessName,
  WixMobileCategory,
  WixMobileDomain,
  WixMobileDomainNF,
  WixMobileGetOwnerApp,
  WixMobileSplit,
  WixMobileWelcome,
  WixStudioAgencyDetails,
  WixStudioBulletSplit,
  WixStudioCategory,
  WixStudioCreate,
  WixStudioCreateExtended,
  WixStudioInputGroup,
  WixStudioInvite,
  WixStudioInviteAccept,
  WixStudioLoader,
  WixStudioMobileSplit,
  WixStudioMobileStores,
  WixStudioMobileWelcome,
  WixStudioSimpleSplit,
  WixStudioSplit,
  WixStudioSplitV2,
  WixStudioSplitV3,
  WixStudioVelo,
  WixStudioWelcome,
  WixStudioWelcomeSwitch,
  WixVibeBulletSplit,
  WixVibeMobileSplit,
  WixVibeMobileSplitFixed,
  WixVibeMobileSplitNavigation,
  WixVibeSplit,
  WixVibeSplitNew,
} from '../../Templates'
import { LoadersGroup } from '../../Templates/WixStudio/WixStudioInvite/Content'
import { once } from '../../utils/once'

const TemplateWithChildren = Template as FC<PropsWithChildren<ComponentProps<typeof Template>>>

export const AppTemplates: FC = () => {
  const [flowContext] = useFlowContext()
  const pageLogger = usePageLogger()
  const [searchParams] = useSearchParams()
  const { templateName, content } = useContent<{ loaders: LoadersGroup }>()

  // This thing will fire once, but if user refreshes the page on welcome screen, it will be sent again and again
  const isStartFlowSent = useRef(false)
  useEffect(() => {
    if (flowContext?.historyEntries && flowContext.historyEntries.length <= 2 && !isStartFlowSent.current) {
      sendStartFlowOnce(pageLogger, flowContext.metadata.ref, searchParams.get('http_referrer') || undefined)
      isStartFlowSent.current = true
    }
  }, [pageLogger, flowContext])

  const promisesStatus = useFinishFlowV2(flowContext?.metadata.forcePlatform)

  if (templateName === 'Wix Studio Invite') {
    if (promisesStatus?.creatingAccountInvites === 'pending') {
      return <WixStudioLoader title={content.loaders.content.sendingInvites} />
    }

    if (promisesStatus?.creatingAccountInvites === 'success') {
      return <WixStudioLoader title={content.loaders.content.preparingWorkspace} />
    }
  }

  return (
    <>
      {/* Lean Template */}
      <TemplateWithChildren name="Sorry">
        <Sorry />
      </TemplateWithChildren>
      <TemplateWithChildren name="Sorry New">
        <SorryNew />
      </TemplateWithChildren>
      <TemplateWithChildren name="Customize Site Equal Split">
        <CustomizeSiteEqualSplit />
      </TemplateWithChildren>

      {/* Harmony */}
      <TemplateWithChildren name="Harmony Template Saved">
        <HarmonyTemplateSaved />
      </TemplateWithChildren>
      <TemplateWithChildren name="Harmony Mobile Block">
        <HarmonyMobileBlock />
      </TemplateWithChildren>
      <TemplateWithChildren name="Harmony Split">
        <HarmonySplit />
      </TemplateWithChildren>

      {/* New Intro Concept */}
      <TemplateWithChildren name="Returning User Split">
        <ReturningUserSplit />
      </TemplateWithChildren>
      <TemplateWithChildren name="Welcome">
        <Welcome />
      </TemplateWithChildren>
      <TemplateWithChildren name="Welcome V2">
        <WelcomeV2 />
      </TemplateWithChildren>
      <TemplateWithChildren name="Category">
        <Category />
      </TemplateWithChildren>
      <TemplateWithChildren name="CategoryNew">
        <CategoryNew />
      </TemplateWithChildren>
      <TemplateWithChildren name="Easier Category">
        <EasierCategory />
      </TemplateWithChildren>
      <TemplateWithChildren name="Old User Split Screen">
        <OldUserSplitScreen />
      </TemplateWithChildren>

      {/* Headless Flow*/}
      <TemplateWithChildren name="Headless Apps">
        <HeadlessApps />
      </TemplateWithChildren>
      <TemplateWithChildren name="Headless Business Name">
        <HeadlessBusinessName />
      </TemplateWithChildren>
      <TemplateWithChildren name="Headless Mobile Site Creation">
        <HeadlessMobileSiteCreation />
      </TemplateWithChildren>
      <TemplateWithChildren name="Headless Radio Input">
        <HeadlessRadioInput />
      </TemplateWithChildren>
      <TemplateWithChildren name="Headless Two Radio Inputs">
        <HeadlessTwoRadioInputs />
      </TemplateWithChildren>

      {/* Wix Studio */}
      <TemplateWithChildren name="Wix Studio Split">
        <WixStudioSplit />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Split V2">
        <WixStudioSplitV2 />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Split V3">
        <WixStudioSplitV3 />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Welcome">
        <WixStudioWelcome />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Agency Details">
        <WixStudioAgencyDetails />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Invite Accept">
        <WixStudioInviteAccept />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Invite">
        <WixStudioInvite />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Create">
        <WixStudioCreate />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Create Extended">
        <WixStudioCreateExtended />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Input Group">
        <WixStudioInputGroup />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Bullet Split">
        <WixStudioBulletSplit />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Simple Split">
        <WixStudioSimpleSplit />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Welcome Switch">
        <WixStudioWelcomeSwitch />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Mobile Stores">
        <WixStudioMobileStores />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Mobile Welcome">
        <WixStudioMobileWelcome />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Mobile Split">
        <WixStudioMobileSplit />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Velo">
        <WixStudioVelo />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Studio Category">
        <WixStudioCategory />
      </TemplateWithChildren>

      {/* Wix Vibe (Picasso) */}
      <TemplateWithChildren name="Wix Vibe Split">
        <WixVibeSplit />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Vibe Split New">
        <WixVibeSplitNew />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Vibe Bullet Split">
        <WixVibeBulletSplit />
      </TemplateWithChildren>

      <TemplateWithChildren name="Wix Vibe Mobile Split">
        <WixVibeMobileSplit />
      </TemplateWithChildren>

      <TemplateWithChildren name="Wix Vibe Mobile Split Navigation">
        <WixVibeMobileSplitNavigation />
      </TemplateWithChildren>

      <TemplateWithChildren name="Wix Vibe Mobile Split Fixed">
        <WixVibeMobileSplitFixed />
      </TemplateWithChildren>

      {/* Stores Funnel */}
      <TemplateWithChildren name="Stores Funnel Choose Category">
        <StoresFunnelChooseCategory />
      </TemplateWithChildren>

      <TemplateWithChildren name="Stores Funnel Choose Template">
        <StoresFunnelChooseTemplate />
      </TemplateWithChildren>

      <TemplateWithChildren name="Stores Funnel Business Name">
        <StoresFunnelBusinessName />
      </TemplateWithChildren>

      <TemplateWithChildren name="Stores Funnel Loader">
        <StoresFunnelLoader />
      </TemplateWithChildren>

      <TemplateWithChildren name="Stores Funnel Upgrade">
        <StoresFunnelUpgrade />
      </TemplateWithChildren>

      {/* Wixel */}
      <TemplateWithChildren name="Wixel Split">
        <WixelSplit />
      </TemplateWithChildren>

      {/* Enterprise */}
      <TemplateWithChildren name="Enterprise Form Welcome">
        <EnterpriseFormWelcome />
      </TemplateWithChildren>
      <TemplateWithChildren name="Enterprise Form Thanks">
        <EnterpriseFormThanks />
      </TemplateWithChildren>
      <TemplateWithChildren name="Enterprise Form">
        <EnterpriseForm />
      </TemplateWithChildren>

      {/* Wix Intros */}
      <TemplateWithChildren name="Wix Intros Box XL">
        <WixIntrosBoxXL />
      </TemplateWithChildren>

      {/* SG */}
      <TemplateWithChildren name="SG Landing Mobile">
        <SGLandingMobile />
      </TemplateWithChildren>

      {/* Mobile */}
      <TemplateWithChildren name="Wix Mobile Welcome">
        <WixMobileWelcome />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Mobile Category">
        <WixMobileCategory />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Mobile Base Checkbox">
        <WixMobileBaseCheckbox />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Mobile Apps">
        <WixMobileApps />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Mobile Business Name">
        <WixMobileBusinessName />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Mobile Domain">
        <WixMobileDomain />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Mobile Domain NF">
        <WixMobileDomainNF />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Mobile Get Owner App">
        <WixMobileGetOwnerApp />
      </TemplateWithChildren>
      <TemplateWithChildren name="Wix Mobile Split">
        <WixMobileSplit />
      </TemplateWithChildren>

      {/* Branded Apps */}
      <TemplateWithChildren name="Branded App Site Selector">
        <BrandedAppSiteSelctor />
      </TemplateWithChildren>
      <TemplateWithChildren name="Branded App Category">
        <BrandedAppCategory />
      </TemplateWithChildren>
      <TemplateWithChildren name="Branded App Welcome">
        <BrandedAppWelcome />
      </TemplateWithChildren>

      <TemplateWithChildren name="Intro Transition">
        <Transition />
      </TemplateWithChildren>
    </>
  )
}

const sendStartFlow = (pageLogger: PageLogger, referralInfo: string, referralUrl?: string) =>
  pageLogger.startFlow({ referralInfo, referralUrl })
const sendStartFlowOnce = once(sendStartFlow)
